import React, {ComponentProps, useCallback, useEffect, useRef, useState} from 'react'
import {FieldRenderProps} from 'react-final-form'
import {useIntl} from 'react-intl'
import {TransitionGroup} from 'react-transition-group'
import Times from '../../../assets/icons/Times.svg'
import {dateOnlyOption, toClientDate, toServerDate} from '../../../helpers/dateUtil'
import Calendar, {CalendarViewType} from '../../common/Calendar'
import FadeTransition from '../../common/FadeTransition'
import OutsideClickHandler from '../../containers/OutsideClickHandler'
import styles from './DateField.scss'

const DateField = (
  {input: {onChange, value},
    required,
    className,
    isDisabled,
    alignRight,
    isClientDate,
    id,
    onMonthChange,
    minYear,
    maxYear,
    calendarOnTop,
    calendarClassName,
    calendarViewType = CalendarViewType.default,
    onGetValueDate,
  }:
    FieldRenderProps<Date, HTMLInputElement> & {
    required?: boolean
    className?: string
    isDisabled: ComponentProps<typeof Calendar>['isDisabled']
    onMonthChange: (date: Date) => any
    alignRight?: boolean
    isClientDate?: boolean
    id?: string
    minYear: number
    maxYear: number
    calendarOnTop: boolean
    calendarClassName?: string
    calendarViewType?: CalendarViewType,
    onGetValueDate?: (date: Date) => void
  }) => {
  const [open, setOpen] = useState(false)
  const intl = useIntl()
  const focus = useCallback(() => {
    setOpen(true)
  }, [])
  const onClose = useCallback(() => {
    setOpen(false)
  }, [])

  let dateValue: Date | undefined
  if (value instanceof Date) dateValue = value
  else {
    const time = Date.parse(value)
    if (!isNaN(time) && isFinite(time)) dateValue = new Date(time)
  }
  if (dateValue && !isClientDate) dateValue = toClientDate(dateValue)
  const selectDay = useCallback(clientDate => {
    onChange(isClientDate ? clientDate : toServerDate(clientDate))
    if (onGetValueDate) {
      onGetValueDate(isClientDate ? clientDate : toServerDate(clientDate))
    }
    setOpen(false)
  }, [onChange, dateValue, isClientDate])
  const inputRef = useRef(null)
  const [inputElm, setInputElm] = useState()
  useEffect(() => void setInputElm(inputRef.current), [])
  const clear = useCallback(() => {
    onChange('')
    onClose()
  }, [onChange, onClose])

  return <div className="position-relative w-100">
    <input
      id={id}
      required={required}
      type="text"
      ref={inputRef}
      className={[className, 'form-control c-pointer w-100'].filter(Boolean).join(' ')}
      readOnly
      value={dateValue ? intl.formatDate(dateValue, dateOnlyOption) : ''}
      placeholder="YYYY/mm/dd"
      onFocus={focus}/>
    <TransitionGroup component={null}>
      {
        !required
        && dateValue
        && <FadeTransition key="clear">
          <Times
            className={['c-pointer position-absolute fa font-size-75', styles.clear].join(' ')}
            onClick={clear}
          />
        </FadeTransition>
      }
      {open && <FadeTransition key="picker">
        <OutsideClickHandler
          excludedElms={[inputElm].filter(Boolean)}
          onOutsideClick={onClose}
          className={[
            !!calendarClassName && calendarClassName,
            styles.picker,
            'rounded shadow bg-white',
            alignRight && styles.alignRight,
            calendarOnTop && styles.calendarOnTop
          ].filter(Boolean).join(' ')}>
          <Calendar
            onChange={selectDay}
            value={dateValue}
            month={dateValue}
            isDisabled={isDisabled}
            onMonthChange={onMonthChange}
            minYear={minYear}
            maxYear={maxYear}
            calendarViewType={calendarViewType}
          />
        </OutsideClickHandler>
      </FadeTransition>}
    </TransitionGroup>
  </div>
}
DateField.displayName = 'DateField'
export default DateField
